import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';

export interface ViewTypeOption {
  type: number;
  label: string;
}

export interface ViewTypePopoverProps {
  options: ViewTypeOption[];
  existingTypes: number[];
  onAdd: (type: number) => void | Promise<void>;
  isPending?: boolean;
  addLabel: string;
  testIdPrefix: string;
}

/**
 * Presentational shell for "add a view of a type not already present" —
 * shared between `features/boards` (AddViewPopover, real board mutation)
 * and `features/project-templates` (TemplateViewPopover, template structure
 * mutation). Owns no data — the caller supplies the mutation via `onAdd`.
 */
export function ViewTypePopover({
  options,
  existingTypes,
  onAdd,
  isPending = false,
  addLabel,
  testIdPrefix,
}: ViewTypePopoverProps) {
  const [open, setOpen] = useState(false);

  const available = options.filter((option) => !existingTypes.includes(option.type));

  async function handleAdd(type: number) {
    await onAdd(type);
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="ml-1 size-7 shrink-0"
              data-testid={`${testIdPrefix}-add-button`}
              aria-label={addLabel}
              disabled={available.length === 0}
            >
              <Plus className="size-3.5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{addLabel}</TooltipContent>
      </Tooltip>

      <PopoverContent
        className="w-48 p-1"
        align="start"
        data-testid={`${testIdPrefix}-add-popover`}
      >
        {available.map((option) => (
          <button
            type="button"
            key={option.type}
            onClick={() => handleAdd(option.type)}
            disabled={isPending}
            className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
            data-testid={`${testIdPrefix}-add-option-${option.type}`}
          >
            {option.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
